import React, { useCallback, useState, useContext } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { SearchOutlined, CachedOutlined } from "@mui/icons-material";
import { Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuthorizationError, useLoader } from "../../hooks";
import { getErrorMessage } from "../form/formUtils";
import {
  userClientDb,
  uiLanguage,
  refreshData,
  userDepartmentId,
} from "../../modules/auth/selectors/userSelector";
import { Alert, TextField, Button } from "../index";
import * as routes from "../../app/routes";
import { HeaderField, ItemField, InfoField, SearchField } from "./index";
import { moduleType } from "../../utils/modules";
import { useInfiniteScrollListStyles } from "./css";
import { IconButton, Divider } from "@mui/material";
import { setUserRefreshData } from "../../modules/auth/slices/userSlice";
import { useAppDispatch } from "../../app/hook";
import { BaseLayoutContext } from "../../contexts/Contexts";
import { Logo } from "../../logo";
import { useColorScheme } from "@mui/material/styles";
import LogoNoRows from "../../logo/empty-light1.svg";
import LogoNoRowsDark from "../../logo/empty-dark1.svg";

interface IInfiniteScrollList {
  loadData?: (params: {
    [name: string]: any;
  }) => Promise<{ data: Array<any>; warnings: Array<any> }>;
  loadDataparams: {
    link?: string;
    linkId?: "operationCode";
    headerColor?: "grey" | "noColor";
    expandRows?: boolean;
    module?: moduleType;
    showOnlyNotCollected?: boolean;
    operationCode?: number | string;
    cellType?: "header" | "info" | "item" | "search";
    data?: any[];
    complete?: boolean;
    ref?: React.RefObject<HTMLDivElement | null>;
  };
  initialData?: Array<any>;
  externalFilter?: boolean;
  externalLoader?: boolean;
  onDataReady?: () => void;
  hideNoRowsLabel?: boolean;
  height?: number;
}

function InfiniteScrollList(props: IInfiniteScrollList) {
  const {
    loadData,
    loadDataparams,
    externalFilter,
    initialData,
    externalLoader,
    onDataReady,
    hideNoRowsLabel,
    height,
  } = props;
  const { mode } = useColorScheme();
  const [, setLoader] = useLoader();
  const [error, setError] = useState({ error: false, message: "", status: 0 });
  const userDb = useSelector(userClientDb);
  const locale = useSelector(uiLanguage);
  const userDbId = userDb ? userDb.id : 0;
  const departmentId = useSelector(userDepartmentId);
  const casheRowNumber = 30;
  const params = {
    ...loadDataparams,
    clientDbId: userDbId,
    locale,
    departmentId,
    top: casheRowNumber,
    pageNumber: 1,
  };
  const [pageNumber, setPageNumber] = useState(1);
  const [dataList, setDataList] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [logoutUser] = useAuthorizationError();
  const [redirect, setRedirect] = useState(false);
  const refresh = useSelector(refreshData);
  const [filter, setFilter] = useState("");
  const { classes, cx } = useInfiniteScrollListStyles();
  const [translate] = useTranslation();
  const dispatch = useAppDispatch();
  const layoutContext = useContext(BaseLayoutContext);

  const getData = (paramsLoading: { [key: string]: any }) => {
    if (initialData) {
      setDataList(initialData);
      setHasMore(false);
      return;
    }

    if (loadData) {
      if (!externalLoader) setLoader(true);

      loadData(paramsLoading)
        .then(({ data }) => {
          if (data.length === 0 && loadDataparams.cellType === "header")
            layoutContext?.setNoRowsInList(true);

          if (loadDataparams && loadDataparams.showOnlyNotCollected === true) {
            const filteredData = data.filter(
              (item) =>
                item.isFullyCollected === 0 || item.isFullyCollected === "0"
            );

            if (filteredData.length) {
              setHasMore(
                filteredData[filteredData.length - 1].Row_number !==
                  filteredData[filteredData.length - 1].totalElements
              );
            } else setHasMore(false);
            setDataList([...paramsLoading.dataList, ...filteredData]);
          } else {
            if (data.length) {
              setHasMore(
                data[data.length - 1].Row_number !==
                  data[data.length - 1].totalElements
              );
            } else setHasMore(false);

            setDataList([...paramsLoading.dataList, ...data]);
          }

          setPageNumber(paramsLoading.pageNumber + 1);
        })
        .then(() => {
          if (!externalLoader) setLoader(false);
          if (onDataReady) onDataReady();
        })
        .catch((reason) => {
          setLoader(false);
          const errMessage = getErrorMessage(reason);

          setError({
            error: true,
            message: errMessage.message,
            status: errMessage.status,
          });
        });
    }
  };

  const isExternalFilterPresent = () => externalFilter && filter !== "";

  React.useEffect(
    () => {
      setPageNumber(1);
      setDataList([]);
      let paramsLoading: { [key: string]: any } = { ...params, dataList: [] };

      if (isExternalFilterPresent()) {
        //some proceduras as a filter takes parameter fillApp, others - fil
        paramsLoading = {
          ...paramsLoading,
          filter: filter,
        };
      }

      getData(paramsLoading);
    }, // eslint-disable-next-line
    [refresh, filter, loadDataparams]
  );

  const fetchMoreData = () => {
    let paramsLoading: { [key: string]: any } = {
      ...params,
      pageNumber,
      dataList,
    };

    if (isExternalFilterPresent()) {
      //some proceduras as a filter takes parameter fillApp, others - fil
      paramsLoading = {
        ...paramsLoading,
        filter: filter,
      };
    }

    getData(paramsLoading);
  };

  const handleClose = () => {
    logoutUser(error.status);

    if (error.status !== 401) {
      setError({ error: false, message: "", status: 0 });
      if (
        !loadDataparams?.operationCode &&
        loadDataparams?.cellType !== "search"
      ) {
        setRedirect(true);
      }
    }
  };

  const showError = (
    <Alert
      isOpen={error.error}
      message={error.message}
      iconColor="error"
      onClose={handleClose}
    />
  );

  const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);
  };

  const handleRefresh = useCallback(() => {
    dispatch(setUserRefreshData(!refresh));
  }, [refresh]);

  const updateRowInList = (updatedRow: any) => {
  setDataList((prev) =>
    prev.map((row) =>
      row.rowNumber === updatedRow.rowNumber ? updatedRow : row
    )
  );
};

  return (
    <div
      className={cx(classes.mainContainer, {
        [classes.itemRows]:
          loadDataparams.cellType === "item" ||
          loadDataparams.cellType === "info",
      })}
    >
      {redirect ? (
        <Navigate
          to={routes.ROUTE_OPTIONS.path}
          state={{ from: routes.ROUTE_LOGIN.path }}
        />
      ) : (
        <div className={classes.container}>
          {error.error && showError}

          {externalFilter && (
            <div className={classes.search}>
              <TextField
                onChange={handleFilter}
                autoComplete="off"
                startAdornment={<SearchOutlined fontSize="small" />}
                name="filter"
                placeholder={translate("toLookFor")}
                fullWidth
              />

              <IconButton onClick={handleRefresh}>
                <CachedOutlined fontSize="small" />
              </IconButton>
            </div>
          )}

          {externalFilter && <Divider />}

          <div
            id="scrollableDiv"
            className={classes.scrollableDiv}
            ref={loadDataparams.ref}
          >
            {dataList.length ? (
              <InfiniteScroll
                dataLength={dataList.length}
                next={fetchMoreData}
                hasMore={hasMore}
                loader={<h4>...</h4>}
                scrollableTarget="scrollableDiv"
                height={height}
              >
                {dataList.map((item, index) => (
                  <div
                    key={index}
                    className={cx({
                      ["zebra-rows"]:
                        loadDataparams && loadDataparams.cellType === "info",
                      ["search-rows"]:
                        loadDataparams && loadDataparams.cellType === "search",
                    })}
                  >
                    {loadDataparams && loadDataparams.cellType === "search" && (
                      <SearchField data={item} {...loadDataparams} />
                    )}

                    {loadDataparams && loadDataparams.cellType === "info" && (
                      <InfoField data={item} />
                    )}
                    {loadDataparams && loadDataparams.cellType === "item" && (
                      <ItemField
                        data={item}
                        selectedRow={
                          loadDataparams.data &&
                          item.rowNumber === loadDataparams.data[0].rowNumber
                        }
                        complete={loadDataparams.complete}
                        updateRowInList={updateRowInList}
                      />
                    )}
                    {loadDataparams && loadDataparams.cellType === "header" && (
                      <HeaderField data={item} {...loadDataparams} />
                    )}
                  </div>
                ))}
              </InfiniteScroll>
            ) : (
              <div className={classes.noRowsContainer}>
                {loadDataparams.cellType === "header" && (
                  <Logo
                    src={mode === "dark" ? LogoNoRowsDark : LogoNoRows}
                    width="170"
                  />
                )}

                <p className={classes.noRows}>
                  {!hideNoRowsLabel && translate("noRows", "Sąrašas tuščias")}
                </p>

                {loadDataparams.cellType === "header" && (
                  <Button
                    color="primary"
                    onClick={layoutContext?.buttonSettings.btnOnClick}
                  >
                    {layoutContext?.buttonSettings.btnText}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default InfiniteScrollList;
